import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Send, Phone, Mail, MapPin } from "lucide-react";
import logo from "@/assets/logo.png";
import { useNavigate } from "react-router-dom";
import { useState } from "react";

const API_URL = import.meta.env.VITE_API_URL;

function Contact() {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });

  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [successOpen, setSuccessOpen] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errorMsg) setErrorMsg("");
  };

  const handleSubmit = async () => {
    if (loading) return;
    setLoading(true);
    setErrorMsg("");

    try {
      const res = await fetch(`${API_URL}/api/public/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          fullName: formData.name,
          email: formData.email,
          phone: formData.phone,
          message: formData.message,
          page: window.location.href,
          source: "NA247 Contact Page",
        }),
      });

      if (!res.ok) throw new Error("Failed to send message. Please try again.");

      setSuccessOpen(true);
      setFormData({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      setErrorMsg(err.message || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-gray-50 min-h-screen">
      <nav className="sticky top-0 z-50 bg-white shadow-sm border-b border-[#f9e2e8]/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <div className="flex items-center">
              <a href="/" className="flex items-center">
                <img src={logo} alt="Logo" className="h-10 sm:h-12 w-auto" />
              </a>
            </div>

            {/* View Properties */}
            <Button
              variant="default"
              className="bg-[#D2138C] hover:bg-pink-700 rounded-full text-white h-10 px-4 text-sm sm:h-11 sm:px-6 sm:text-base"
              onClick={() => navigate("/properties")}>
              View Accommodation
            </Button>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-12">
        <div className="text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-3">
            Get in touch
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto">
            Have questions? We're here to help you find your perfect home
          </p>
        </div>

        <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-12">
          {/* Enquire Form */}
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5 sm:p-8">
            <form
              className="space-y-3 sm:space-y-4"
              onSubmit={(e) => {
                e.preventDefault();
                handleSubmit();
              }}>
              <div>
                <Label htmlFor="name">Full Name</Label>
                <Input
                  id="name"
                  name="name"
                  placeholder="Enter your name"
                  required
                  className="mt-1.5"
                  value={formData.name}
                  onChange={handleChange}
                />
              </div>

              <div>
                <Label htmlFor="email">Email Address</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="Enter your email"
                  required
                  className="mt-1.5"
                  value={formData.email}
                  onChange={handleChange}
                />
              </div>

              <div>
                <Label htmlFor="phone">Phone Number</Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  placeholder="Enter your phone number"
                  className="mt-1.5"
                  value={formData.phone}
                  onChange={handleChange}
                />
              </div>

              <div>
                <Label htmlFor="message">Message</Label>
                <Textarea
                  id="message"
                  name="message"
                  placeholder="Type your message or questions here..."
                  rows={5}
                  required
                  className="mt-1.5"
                  value={formData.message}
                  onChange={handleChange}
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-[#D2138C] hover:bg-pink-700 text-white rounded-xl h-11 cursor-pointer disabled:opacity-70">
                <Send className="w-4 h-4 mr-2" />
                {loading ? "Sending..." : "Send Message"}
              </Button>

              {errorMsg && (
                <p className="text-red-600 text-sm text-center">{errorMsg}</p>
              )}
            </form>

            {successOpen && (
              <div
                className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 px-4"
                onClick={() => setSuccessOpen(false)}>
                <div
                  className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl"
                  onClick={(e) => e.stopPropagation()}>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Message Sent
                  </h3>
                  <p className="mt-2 text-sm text-gray-600">
                    Thanks for reaching out. We’ve received your message and
                    we’ll get back to you within 24 hours.
                  </p>

                  <div className="mt-5 flex flex-col-reverse sm:flex-row justify-end gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full sm:w-auto"
                      onClick={() => setSuccessOpen(false)}>
                      Close
                    </Button>

                    <Button
                      type="button"
                      className="w-full sm:w-auto bg-[#D2138C] hover:bg-[#B01076] text-white"
                      onClick={() => setSuccessOpen(false)}>
                      OK
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Contact Information */}
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5 sm:p-8">
            <h2 className="text-gray-900 text-xl font-normal mb-6">
              Contact Information
            </h2>

            <div className="space-y-6 mb-8">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center flex-shrink-0">
                  <Phone className="w-6 h-6 text-[#D2138C]" />
                </div>
                <div>
                  <h4 className="text-gray-900 mb-1">Phone</h4>
                  <p className="text-gray-600">Nurseassist247</p>
                  <p className="text-gray-600">08 9545 6010</p>
                  <p className="text-sm text-gray-500 mt-1">
                    Mon-Fri, 9am-5pm AEST
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center flex-shrink-0">
                  <Mail className="w-6 h-6 text-[#D2138C]" />
                </div>
                <div>
                  <h4 className="text-gray-900 mb-1">Email</h4>
                  <p className="text-gray-600">admin@nurseassist247.com.au</p>
                  <p className="text-sm text-gray-500 mt-1">
                    We respond within 24 hours
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-6 h-6 text-[#D2138C]" />
                </div>
                <div>
                  <h4 className="text-gray-900 mb-1">Office Address</h4>
                  <p className="text-gray-600">
                    75/95 Chalgrove Avenue,
                    <br />
                    Rockingham, 6168, Western Australia
                    <br />
                    (Across from Dowling Street Reserve)
                  </p>
                </div>
              </div>
            </div>
            {/* Additional Info Card */}
            <div className="mt-8 bg-[#f9e2e8]/30 rounded-2xl p-5 sm:p-6">
              <h4 className="text-gray-900 mb-3">NDIS Registered Provider</h4>
              <p className="text-gray-600 text-sm leading-relaxed">
                We are a registered NDIS provider committed to delivering
                quality SDA accommodation services. Our team has extensive
                experience in matching participants with homes that support
                their goals and enhance their independence.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Contact;
